import {
	type Dispatch,
	type RefObject,
	type SetStateAction,
	useCallback,
	useRef,
} from "react";
import {
	type CanvasCard,
	type CanvasTool,
	MIN_CARD_HEIGHT,
	MIN_CARD_WIDTH,
	type ResizeDirection,
} from "./canvas-utils";

interface UseCanvasInteractionsOptions {
	canvasRef: RefObject<HTMLDivElement | null>;
	zoom: number;
	canvasOffset: { x: number; y: number };
	setCanvasOffset: Dispatch<SetStateAction<{ x: number; y: number }>>;
	setCards: Dispatch<SetStateAction<CanvasCard[]>>;
	canvasTool: CanvasTool;
	isCommentMode: boolean;
	placePendingComment: (x: number, y: number) => void;
}

/** Mouse gestures on the canvas: pan, card drag, card resize, comment placement. */
export function useCanvasInteractions({
	canvasRef,
	zoom,
	canvasOffset,
	setCanvasOffset,
	setCards,
	canvasTool,
	isCommentMode,
	placePendingComment,
}: UseCanvasInteractionsOptions) {
	// Refs for pan / drag (avoid re-renders during gesture)
	const isPanning = useRef(false);
	const panStart = useRef({ x: 0, y: 0 });
	const offsetAtPanStart = useRef({ x: 0, y: 0 });

	const isDraggingCard = useRef(false);
	const dragCardId = useRef<string | null>(null);
	const dragCardStart = useRef({ x: 0, y: 0 });
	const dragCardOrigin = useRef({ x: 0, y: 0 });

	// Resize refs
	const isResizing = useRef(false);
	const resizeCardId = useRef<string | null>(null);
	const resizeDirection = useRef<ResizeDirection>("se");
	const resizeStart = useRef({ x: 0, y: 0 });
	const resizeOrigin = useRef({ x: 0, y: 0, w: 0, h: 0 });

	const handleCanvasMouseDown = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const target = e.target as HTMLElement;
			const isCanvasBg =
				target === e.currentTarget || target.hasAttribute("data-canvas-bg");

			// --- Comment mode: place a comment on click (anywhere) ---
			if (isCommentMode && e.button === 0) {
				e.preventDefault();
				const rect = canvasRef.current?.getBoundingClientRect();
				if (!rect) return;
				// Convert client coords → canvas-space coords
				const canvasX = (e.clientX - rect.left - canvasOffset.x) / zoom;
				const canvasY = (e.clientY - rect.top - canvasOffset.y) / zoom;
				placePendingComment(canvasX, canvasY);
				return;
			}

			// Middle-click always pans; left-click on background only pans in hand mode
			if (
				e.button === 1 ||
				(e.button === 0 && isCanvasBg && canvasTool === "hand")
			) {
				e.preventDefault();
				isPanning.current = true;
				panStart.current = { x: e.clientX, y: e.clientY };
				offsetAtPanStart.current = { ...canvasOffset };
				if (canvasRef.current) {
					canvasRef.current.style.cursor = "grabbing";
				}
			}
		},
		[canvasOffset, canvasTool, isCommentMode, zoom, placePendingComment],
	);

	const handleCanvasMouseMove = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			// ── Card resize ──
			if (isResizing.current && resizeCardId.current) {
				const dx = (e.clientX - resizeStart.current.x) / zoom;
				const dy = (e.clientY - resizeStart.current.y) / zoom;
				const dir = resizeDirection.current;
				const { x: ox, y: oy, w: ow, h: oh } = resizeOrigin.current;

				let newX = ox,
					newY = oy,
					newW = ow,
					newH = oh;

				if (dir.includes("e")) newW = Math.max(MIN_CARD_WIDTH, ow + dx);
				if (dir.includes("w")) {
					newW = Math.max(MIN_CARD_WIDTH, ow - dx);
					newX = ox + (ow - newW);
				}
				if (dir.includes("s")) newH = Math.max(MIN_CARD_HEIGHT, oh + dy);
				if (dir.includes("n")) {
					newH = Math.max(MIN_CARD_HEIGHT, oh - dy);
					newY = oy + (oh - newH);
				}

				setCards((prev) =>
					prev.map((c) =>
						c.id === resizeCardId.current
							? { ...c, x: newX, y: newY, width: newW, height: newH }
							: c,
					),
				);
				return;
			}

			// ── Card drag ──
			if (isDraggingCard.current && dragCardId.current) {
				const dx = (e.clientX - dragCardStart.current.x) / zoom;
				const dy = (e.clientY - dragCardStart.current.y) / zoom;
				setCards((prev) =>
					prev.map((c) =>
						c.id === dragCardId.current
							? {
								...c,
								x: dragCardOrigin.current.x + dx,
								y: dragCardOrigin.current.y + dy,
							}
							: c,
					),
				);
				return;
			}

			// ── Canvas pan ──
			if (!isPanning.current) return;
			const dx = e.clientX - panStart.current.x;
			const dy = e.clientY - panStart.current.y;
			setCanvasOffset({
				x: offsetAtPanStart.current.x + dx,
				y: offsetAtPanStart.current.y + dy,
			});
		},
		[zoom],
	);

	const handleCanvasMouseUp = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (isPanning.current) {
				isPanning.current = false;
				if (canvasRef.current) {
					canvasRef.current.style.cursor = isCommentMode
						? "crosshair"
						: canvasTool === "hand"
							? "grab"
							: "default";
				}
			}
			if (isDraggingCard.current) {
				isDraggingCard.current = false;
				dragCardId.current = null;
			}
			if (isResizing.current) {
				isResizing.current = false;
				resizeCardId.current = null;
			}
		},
		[isCommentMode, canvasTool],
	);

	const handleCardDragStart = useCallback(
		(e: React.MouseEvent, card: CanvasCard) => {
			e.stopPropagation();
			e.preventDefault();
			isDraggingCard.current = true;
			dragCardId.current = card.id;
			dragCardStart.current = { x: e.clientX, y: e.clientY };
			dragCardOrigin.current = { x: card.x, y: card.y };
		},
		[],
	);

	const handleResizeStart = useCallback(
		(e: React.MouseEvent, card: CanvasCard, direction: ResizeDirection) => {
			e.stopPropagation();
			e.preventDefault();
			isResizing.current = true;
			resizeCardId.current = card.id;
			resizeDirection.current = direction;
			resizeStart.current = { x: e.clientX, y: e.clientY };
			resizeOrigin.current = {
				x: card.x,
				y: card.y,
				w: card.width,
				h: card.height,
			};
		},
		[],
	);

	return {
		handleCanvasMouseDown,
		handleCanvasMouseMove,
		handleCanvasMouseUp,
		handleCardDragStart,
		handleResizeStart,
	};
}
